import clsx from 'clsx'
import React from 'react'
import ReactCalendar, {
  CalendarProps as ReactCalendarProps,
} from 'react-calendar'

import s from './styles.module.scss'

import { TileContent } from './components/tile-content/TileContent'
import { computeTileClassName } from './helpers/compute-tile-class-name.helper'
import { handleOnCalendarSelectDate } from './helpers/handle-on-calendar-select-date'
import { isTileDisabled } from './helpers/is-tile-disabled.helper'
import useTimezoneConverter, { IDates } from './useTimezoneConverter'
import IconSvg from '../../atoms/icons/icon-svg/IconSvg'
import { useTravelTypeContext } from '../../contexts'
import { useLocale } from '../../../hooks/use-locale'
import {
  HouseAvailabilityUnionOutputDto,
  HouseContext,
  TravelTypeEnum,
} from '../../../lib/graphql'
import { convertTimezonedDateToIsoAtMidnight as utcStartOfDay } from '../../../utils/date-helpers'

const BASE_CALENDAR_PROPS: ReactCalendarProps = {
  minDate: new Date(),
  next2Label: <></>,
  prev2Label: <></>,
  minDetail: 'month',
  maxDetail: 'month',
  defaultView: 'month',
  nextLabel: <IconSvg.IconCalendarNext />,
  prevLabel: <IconSvg.IconCalendarPrev />,
  showDoubleView: true,
  showNeighboringMonth: false,
  allowPartialRange: true,
}

export interface IDatesCalendarProps {
  activeStartDate: Date
  setActiveStartDate: React.Dispatch<React.SetStateAction<Date>>
  houseAvailabilities: HouseAvailabilityUnionOutputDto[]
}

export type DatesCalendarProps = IDates &
  IDatesCalendarProps & {
    setDate: (dates: IDates) => void
    closeModal?: () => void
    house?: Pick<HouseContext, 'arrival' | 'departure'>
    disableSelection?: boolean
    shouldDisplayHourPickers?: boolean
    revampPicker?: boolean
  }
const DatesCalendar = ({
  startDate: _startDate,
  endDate: _endDate,
  setDate: _setDate,
  closeModal,
  house,
  activeStartDate,
  setActiveStartDate,
  houseAvailabilities,
  disableSelection = false,
  shouldDisplayHourPickers = false,
  revampPicker = false,
}: DatesCalendarProps) => {
  const { locale } = useLocale()
  const { travelType } = useTravelTypeContext()

  const { startDate, endDate, setDate } = useTimezoneConverter({
    startDate: _startDate,
    endDate: _endDate,
    setDate: _setDate,
    houseAvailabilities,
  })

  const payloadToCompute = {
    house,
    houseAvailabilities,
    travelType,
    startDate,
    endDate,
  }

  const allowMultiDates = travelType !== TravelTypeEnum.CORPORATE_EVENING_EVENT

  return (
    <ReactCalendar
      tileClassName={({ date }) =>
        computeTileClassName({ date: utcStartOfDay(date), ...payloadToCompute })
      }
      tileDisabled={({ date }: { date: Date }) =>
        isTileDisabled({ date: utcStartOfDay(date), ...payloadToCompute })
      }
      tileContent={({ date }) => (
        <TileContent date={date} {...payloadToCompute} />
      )}
      value={
        startDate
          ? allowMultiDates
            ? [startDate, endDate ?? null]
            : startDate
          : [null, null]
      }
      onChange={(value) =>
        handleOnCalendarSelectDate({
          setDate,
          shouldDisplayHourPickers,
          travelType,
          value,
          closeModal,
          startDate,
        })
      }
      onActiveStartDateChange={({ action, activeStartDate }) => {
        if (action === 'onChange' || !activeStartDate) {
          return
        }

        setActiveStartDate(activeStartDate)
      }}
      className={clsx(s.calendar, { [s.revampPicker]: revampPicker })}
      activeStartDate={activeStartDate}
      selectRange={allowMultiDates && !disableSelection}
      locale={locale}
      {...BASE_CALENDAR_PROPS}
    />
  )
}

export default DatesCalendar
